import { z } from 'zod';

import { BaseFlowBlockSchema, BlockType } from '../base/baseSchema.js';

export enum LlmModelName {
  amazon_nova_pro = 'amazon.nova-pro-v1:0',
  claude_3_5_sonnet_v2 = 'anthropic.claude-3-5-sonnet-20241022-v2:0',
  claude_3_7_sonnet_v1 = 'anthropic.claude-3-7-sonnet-20250219-v1:0',
  claude_opus_4 = 'anthropic.claude-opus-4',
  claude_sonnet_4 = 'anthropic.claude-sonnet-4',
  deepseek_r1 = 'deepseek-r1',
  deepseek_v3 = 'deepseek-ai/DeepSeek-V3',
  gemini_1_5_pro = 'gemini-1.5-pro-002',
  gemini_1_5_flash = 'gemini-1.5-flash-002',
  gemini_2_0_flash_001 = 'gemini-2.0-flash-001',
  gemini_2_0_flash_thinking_exp_01_21 = 'gemini-2.0-flash-thinking-exp-01-21',
  gemini_2_5_flash = 'gemini-2.5-flash',
  gemini_2_5_pro_preview_03_25 = 'gemini-2.5-pro-preview-03-25',
  gemini_2_5_pro = 'gemini-2.5-pro',
  gemini_3_pro_preview = 'gemini-3-pro-preview',
  gemini_3_flash_preview = 'gemini-3-flash-preview',
  gemini_3_1_pro_preview = 'gemini-3.1-pro-preview',
  gemini_3_1_flash_lite_preview = 'gemini-3.1-flash-lite-preview',
  gpt_4_1 = 'gpt-4.1',
  gpt_4_1_mini = 'gpt-4.1-mini',
  gpt_4_1_nano = 'gpt-4.1-nano',
  gpt_4o = 'gpt-4o',
  gpt_4o_mini = 'gpt-4o-mini',
  gpt_5 = 'gpt-5',
  gpt_5_mini = 'gpt-5-mini',
  gpt_5_nano = 'gpt-5-nano',
  gpt_5_1 = 'gpt-5.1',
  gpt_5_2 = 'gpt-5.2',
  gpt_5_4 = 'gpt-5.4',
  claude_opus_4_1 = 'anthropic.claude-opus-4-1-20250805-v1:0',
  claude_opus_4_5 = 'anthropic.claude-opus-4-5-20251015-v1:0',
  claude_haiku_4_5 = 'anthropic.claude-haiku-4-5-20251015-v1:0',
  claude_sonnet_4_5 = 'anthropic.claude-sonnet-4-5-20250929-v1:0',
  claude_opus_4_6 = 'anthropic.claude-opus-4-6-v1',
  claude_sonnet_4_6 = 'anthropic.claude-sonnet-4-6',
  meta_llama_3_2_11b = 'meta.llama3-2-11b-instruct-v1:0',
  meta_llama_3_3_70b = 'meta.llama3-3-70b-instruct-v1:0',
  o1 = 'o1',
  o3 = 'o3',
  o1_mini = 'o1-mini',
  o3_mini = 'o3-mini',
  o4_mini = 'o4-mini',
  notable_default = 'notable-default',
}

export enum LlmModelStatus {
  Active = 'Active',
  Deprecated = 'Deprecated',
}

export enum LlmModelModality {
  Text = 'Text',
  File = 'File',
}

export interface LlmModel {
  id: LlmModelName;
  name: string;
  status: LlmModelStatus;
  modalities: LlmModelModality[];
  supportsTemperatureAndTopP: boolean;
}

export const LLM_MODELS: Record<LlmModelName, LlmModel> = {
  [LlmModelName.notable_default]: {
    id: LlmModelName.notable_default,
    name: 'Auto',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.gemini_1_5_flash]: {
    id: LlmModelName.gemini_1_5_flash,
    name: 'Gemini 1.5 Flash',
    status: LlmModelStatus.Deprecated,
    modalities: [LlmModelModality.Text],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.gemini_1_5_pro]: {
    id: LlmModelName.gemini_1_5_pro,
    name: 'Gemini 1.5 Pro',
    status: LlmModelStatus.Deprecated,
    modalities: [LlmModelModality.Text],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.gemini_2_0_flash_001]: {
    id: LlmModelName.gemini_2_0_flash_001,
    name: 'Gemini 2.0 Flash 001',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.gemini_2_0_flash_thinking_exp_01_21]: {
    id: LlmModelName.gemini_2_0_flash_thinking_exp_01_21,
    name: 'Gemini 2.0 Flash Thinking Exp 01-21',
    status: LlmModelStatus.Deprecated,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.gemini_2_5_flash]: {
    id: LlmModelName.gemini_2_5_flash,
    name: 'Gemini 2.5 Flash',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.gemini_2_5_pro_preview_03_25]: {
    id: LlmModelName.gemini_2_5_pro_preview_03_25,
    name: 'Gemini 2.5 Pro Preview 03-25',
    status: LlmModelStatus.Deprecated,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.gemini_2_5_pro]: {
    id: LlmModelName.gemini_2_5_pro,
    name: 'Gemini 2.5 Pro',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.gemini_3_pro_preview]: {
    id: LlmModelName.gemini_3_pro_preview,
    name: 'Gemini 3 Pro Preview',
    status: LlmModelStatus.Deprecated,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.gemini_3_flash_preview]: {
    id: LlmModelName.gemini_3_flash_preview,
    name: 'Gemini 3 Flash Preview',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.gemini_3_1_pro_preview]: {
    id: LlmModelName.gemini_3_1_pro_preview,
    name: 'Gemini 3.1 Pro Preview',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.gemini_3_1_flash_lite_preview]: {
    id: LlmModelName.gemini_3_1_flash_lite_preview,
    name: 'Gemini 3.1 Flash-Lite Preview',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.claude_3_5_sonnet_v2]: {
    id: LlmModelName.claude_3_5_sonnet_v2,
    name: 'Claude 3.5 Sonnet V2',
    status: LlmModelStatus.Deprecated,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.claude_3_7_sonnet_v1]: {
    id: LlmModelName.claude_3_7_sonnet_v1,
    name: 'Claude 3.7 Sonnet',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.claude_sonnet_4]: {
    id: LlmModelName.claude_sonnet_4,
    name: 'Claude Sonnet 4',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.claude_opus_4]: {
    id: LlmModelName.claude_opus_4,
    name: 'Claude Opus 4',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.claude_opus_4_1]: {
    id: LlmModelName.claude_opus_4_1,
    name: 'Claude Opus 4.1',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: false,
  },
  [LlmModelName.claude_opus_4_5]: {
    id: LlmModelName.claude_opus_4_5,
    name: 'Claude Opus 4.5',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.claude_haiku_4_5]: {
    id: LlmModelName.claude_haiku_4_5,
    name: 'Claude Haiku 4.5',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.claude_sonnet_4_5]: {
    id: LlmModelName.claude_sonnet_4_5,
    name: 'Claude Sonnet 4.5',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: false,
  },
  [LlmModelName.claude_opus_4_6]: {
    id: LlmModelName.claude_opus_4_6,
    name: 'Claude Opus 4.6',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: false,
  },
  [LlmModelName.claude_sonnet_4_6]: {
    id: LlmModelName.claude_sonnet_4_6,
    name: 'Claude Sonnet 4.6',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: false,
  },
  [LlmModelName.gpt_4_1]: {
    id: LlmModelName.gpt_4_1,
    name: 'GPT-4.1',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.gpt_4_1_mini]: {
    id: LlmModelName.gpt_4_1_mini,
    name: 'GPT-4.1-mini',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.gpt_4_1_nano]: {
    id: LlmModelName.gpt_4_1_nano,
    name: 'GPT-4.1-nano',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.gpt_4o_mini]: {
    id: LlmModelName.gpt_4o_mini,
    name: 'GPT-4o-mini',
    status: LlmModelStatus.Deprecated,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.gpt_4o]: {
    id: LlmModelName.gpt_4o,
    name: 'GPT-4o',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.gpt_5]: {
    id: LlmModelName.gpt_5,
    name: 'GPT-5',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: false,
  },
  [LlmModelName.gpt_5_mini]: {
    id: LlmModelName.gpt_5_mini,
    name: 'GPT-5-mini',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: false,
  },
  [LlmModelName.gpt_5_nano]: {
    id: LlmModelName.gpt_5_nano,
    name: 'GPT-5-nano',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: false,
  },
  [LlmModelName.gpt_5_1]: {
    id: LlmModelName.gpt_5_1,
    name: 'GPT-5.1',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: false,
  },
  [LlmModelName.gpt_5_2]: {
    id: LlmModelName.gpt_5_2,
    name: 'GPT-5.2',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: false,
  },
  [LlmModelName.gpt_5_4]: {
    id: LlmModelName.gpt_5_4,
    name: 'GPT-5.4',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text, LlmModelModality.File],
    supportsTemperatureAndTopP: false,
  },
  [LlmModelName.o1]: {
    id: LlmModelName.o1,
    name: 'o1',
    status: LlmModelStatus.Deprecated,
    modalities: [LlmModelModality.Text],
    supportsTemperatureAndTopP: false,
  },
  [LlmModelName.o3]: {
    id: LlmModelName.o3,
    name: 'o3',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text],
    supportsTemperatureAndTopP: false,
  },
  [LlmModelName.o1_mini]: {
    id: LlmModelName.o1_mini,
    name: 'o1-mini',
    status: LlmModelStatus.Deprecated,
    modalities: [LlmModelModality.Text],
    supportsTemperatureAndTopP: false,
  },
  [LlmModelName.o3_mini]: {
    id: LlmModelName.o3_mini,
    name: 'o3-mini',
    status: LlmModelStatus.Deprecated,
    modalities: [LlmModelModality.Text],
    supportsTemperatureAndTopP: false,
  },
  [LlmModelName.o4_mini]: {
    id: LlmModelName.o4_mini,
    name: 'o4-mini',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text],
    supportsTemperatureAndTopP: false,
  },
  [LlmModelName.meta_llama_3_2_11b]: {
    id: LlmModelName.meta_llama_3_2_11b,
    name: 'Meta Llama 3.2 11B',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.meta_llama_3_3_70b]: {
    id: LlmModelName.meta_llama_3_3_70b,
    name: 'Meta Llama 3.3 70B',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.amazon_nova_pro]: {
    id: LlmModelName.amazon_nova_pro,
    name: 'Amazon Nova Pro',
    status: LlmModelStatus.Active,
    modalities: [LlmModelModality.Text],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.deepseek_r1]: {
    id: LlmModelName.deepseek_r1,
    name: 'DeepSeek R1',
    status: LlmModelStatus.Deprecated,
    modalities: [LlmModelModality.Text],
    supportsTemperatureAndTopP: true,
  },
  [LlmModelName.deepseek_v3]: {
    id: LlmModelName.deepseek_v3,
    name: 'DeepSeek V3',
    status: LlmModelStatus.Deprecated,
    modalities: [LlmModelModality.Text],
    supportsTemperatureAndTopP: true,
  },
};

export const StructuredOutputFieldBaseSchema = z.object({
  fieldLabel: z.string(),
  definition: z.string(),
});

export type StructuredOutputFieldBase = z.infer<typeof StructuredOutputFieldBaseSchema>;

export const MAX_KNOWLEDGE_BASE_FILES = 10;

export const LlmPromptBlockConfigSchema = z.object({
  prompt: z.string().min(1, 'Prompt is required'),
  fileReference: z.string().optional(),
  fileReferences: z.array(z.string()).max(MAX_KNOWLEDGE_BASE_FILES).optional(),
  modelName: z.nativeEnum(LlmModelName, { message: 'Model name is required' }),
  temperature: z
    .number()
    .min(0, 'Temperature must be between 0 and 2')
    .max(2, 'Temperature must be between 0 and 2')
    .optional(),
  maxTokens: z
    .number()
    .int()
    .min(30, 'Max tokens must be greater than 30')
    .max(16384, 'Max tokens must be less than 16384')
    .optional(),
  topP: z
    .number()
    .min(0, 'Top P must be between 0 and 1')
    .max(1, 'Top P must be between 0 and 1')
    .optional(),
  frequencyPenalty: z
    .number()
    .min(-2, 'Frequency penalty must be between -2 and 2')
    .max(2, 'Frequency penalty must be between -2 and 2')
    .optional(),
  responseFormat: z.array(StructuredOutputFieldBaseSchema).optional().default([]),
});

export const LlmPromptBlockBaseSchema = z.object({
  type: z.literal(BlockType.LlmPrompt),
  config: LlmPromptBlockConfigSchema,
});

export const LlmPromptBlockSchema = BaseFlowBlockSchema.merge(LlmPromptBlockBaseSchema);

export type LlmPromptBlock = z.infer<typeof LlmPromptBlockSchema>;
